import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../Header';
import Footer from '../Footer';
import '../../styles/CommonStyles.css';
import publicService from '../../services/publicService';

function ProfessionalsPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSpecialization, setSelectedSpecialization] = useState('All');
  const [sortBy, setSortBy] = useState('name');
  const [memberListingPrice, setMemberListingPrice] = useState(0);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isMember, setIsMember] = useState(false);
  const [showNotification, setShowNotification] = useState(true);
  const [notificationClosed, setNotificationClosed] = useState(false);
  const [professionals, setProfessionals] = useState([]);
  const [specializations, setSpecializations] = useState(['All']);
  const [visibleEmails, setVisibleEmails] = useState({});
  const navigate = useNavigate();
  
  // Check if user is logged in and is a member
  useEffect(() => {
    const token = localStorage.getItem('memberToken') || sessionStorage.getItem('memberToken');
    const memberType = localStorage.getItem('memberType');
    
    if (token) {
      setIsLoggedIn(true);
      // Updated membership check: include all valid member types
      setIsMember(memberType && ['primary_member', 'associate_member', 'student_membership', 'financial_collegium','academic_collegium','institutional_collegium'].includes(memberType.toLowerCase()));
    } else {
      setIsLoggedIn(false);
      setIsMember(false);
    }
    
    // Check if notification was manually closed before (stored in sessionStorage)
    const notificationStatus = sessionStorage.getItem('listingNotificationClosed');
    if (notificationStatus === 'true') {
      setShowNotification(false);
      setNotificationClosed(true);
    }
    
    // Fetch professionals and pricing data
    fetchProfessionals();
  }, []);
  
  const fetchProfessionals = async () => {
    try {
      // Fetch professionals data
      const response = await publicService.getProfessionals();
      setProfessionals(response.professionals);
      
      // Extract unique specializations
      const uniqueSpecializations = ['All', ...new Set(response.professionals.map(p => p.specialization))];
      setSpecializations(uniqueSpecializations);
      
      // Get listing price for members
      setMemberListingPrice(response.memberPrice ?? 7500);
    } catch (error) {
      console.error('Error fetching professionals:', error);
    }
  };
  
  // Get the listing price for members
  const getListingPrice = () => {
    return memberListingPrice;
  };
  
  const handleAddListing = () => {
    if (!isLoggedIn) {
      navigate('/member-login', { state: { returnUrl: '/professionals/add-listing' }});
      return;
    }
    
    // Only members can add listings
    if (!isMember) {
      alert('Only BVA members can add a listing. Please join BVA to list your profile.');
      navigate('/membership');
      return;
    }
    
    navigate('/professionals/add-listing');
  };
  
  const closeNotification = () => {
    setShowNotification(false);
    setNotificationClosed(true);
    sessionStorage.setItem('listingNotificationClosed', 'true');
  };
  
  // Toggle email visibility for a specific professional
  const toggleEmailVisibility = (professionalId) => {
    setVisibleEmails(prev => ({
      ...prev,
      [professionalId]: !prev[professionalId]
    }));
  };
  
  // Replace the handleContactClick function with this improved version
  const handleContactClick = (e, professional) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Force email visibility when contacting
    if (!visibleEmails[professional.id]) {
      toggleEmailVisibility(professional.id);
    }
    
    // Check if email exists
    if (!professional.email) {
      alert("This professional's email is not available.");
      return;
    }
    
    try {
      // Create email subject and body
      const subject = `Inquiry via BVA Professional Directory - ${professional.name}`;
      const body = `Dear ${professional.name},\n\nI found your profile on the Bangalore Valuers' Association Professional Directory and would like to connect regarding valuation services.\n\n[Your message here]\n\nBest regards,\n[Your name]`;
      
      // Create mailto URL
      const mailtoLink = `mailto:${encodeURIComponent(professional.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      
      // Try to open email client
      window.location.href = mailtoLink;
      
      // Fallback for browsers that don't support mailto
      setTimeout(() => {
        // If still on the same page after a short delay, provide alternative
        if (document.hasFocus()) {
          // Show the email directly so user can copy it
          alert(`It seems your browser couldn't open the email client automatically.\n\nYou can contact ${professional.name} directly at: ${professional.email}`);
        }
      }, 500);
    } catch (error) {
      console.error("Error opening email client:", error);
      alert(`You can contact ${professional.name} directly at: ${professional.email}`);
    }
  };
  
  // Filter and sort professionals
  const filteredAndSortedProfessionals = professionals
    .filter(professional => {
      const matchesSearch = professional.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                            professional.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            professional.specialization.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesSpecialization = selectedSpecialization === 'All' || professional.specialization === selectedSpecialization;
      
      return matchesSearch && matchesSpecialization;
    })
    .sort((a, b) => {
      if (sortBy === 'name') {
        return a.name.localeCompare(b.name);
      } else if (sortBy === 'location') {
        return a.location.localeCompare(b.location);
      } else if (sortBy === 'experience') {
        return parseInt(b.experience) - parseInt(a.experience);
      }
      return 0;
    });
  
  return (
    <div className="page-container">
      <Header />
      
      <div className="page-header">
        <h1>PROFESSIONALS</h1>
        <p>Connect with experienced valuation professionals in our network</p>
      </div>
      
      {/* Professional Listings Disclaimer */}
      <div className="disclaimer-section" style={{
        backgroundColor: '#f8f9fa',
        border: '1px solid #dee2e6',
        borderRadius: '8px',
        margin: '0 2rem 2rem',
        padding: '1.5rem'
      }}>
        <h3 style={{
          color: 'var(--primary-color)',
          marginBottom: '1rem',
          fontSize: '1.1rem',
          fontWeight: '600'
        }}>
          Disclaimer on Professional Listings
        </h3>
        <div style={{
          fontSize: '0.9rem',
          lineHeight: '1.6',
          color: '#555'
        }}>
          <p style={{ marginBottom: '1rem' }}>
            The profiles and firm details listed are solely for informational and networking purposes within the valuation community and do not constitute advertisement or solicitation.
          </p>
          <p style={{ marginBottom: '1rem' }}>
            Bangalore Valuers' Association (BVA) does not endorse any individual or entity. A nominal fee is collected only for hosting and maintenance.
          </p>
          <p style={{ marginBottom: '0.5rem', fontWeight: '500' }}>
            Members must ensure that:
          </p>
          <ul style={{
            paddingLeft: '1.5rem',
            marginBottom: '1rem'
          }}>
            <li style={{ marginBottom: '0.3rem' }}>Information is factual and non-promotional,</li>
            <li style={{ marginBottom: '0.3rem' }}>Firm names are highlighted, with individual details limited to qualifications and registrations,</li>
            <li style={{ marginBottom: '0.3rem' }}>No references to clients, testimonials, or achievements are included.</li>
          </ul>
          <p style={{ 
            fontWeight: '500',
            color: '#444',
            fontSize: '0.85rem'
          }}>
            By submitting a profile, members confirm adherence to their applicable Code of Conduct. BVA is not liable for any non-compliance.
          </p>
        </div>
      </div>
      
      {showNotification && (
        <div className="notification" style={{
          backgroundColor: 'var(--secondary-color)',
          padding: '1rem',
          margin: '0 2rem 2rem',
          borderRadius: '8px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <strong>Get listed with us!</strong> 
            {isMember ? (
              <>Showcase your professional profile {getListingPrice() > 0 ? `for just ₹${getListingPrice().toLocaleString('en-IN')}` : 'for free'}</>
            ) : (
              <>Only BVA members can add a listing. Please join BVA to list your profile.</>
            )}
          </div>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button 
              className="btn btn-primary" 
              style={{ 
                backgroundColor: 'var(--primary-color)', 
                color: 'white'
              }}
              onClick={handleAddListing}
            >
              {isMember ? 'Add Your Listing' : 'Join BVA'}
            </button>
            <button 
              onClick={closeNotification}
              style={{ 
                background: 'none', 
                border: 'none', 
                cursor: 'pointer', 
                fontSize: '1.2rem'
              }}
            >
              &times;
            </button>
          </div>
        </div>
      )}
      
      <section className="content-section">
        <div className="search-filter-container" style={{ 
          display: 'flex', 
          flexWrap: 'wrap',
          gap: '1rem',
          marginBottom: '2rem',
          alignItems: 'center',
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
        }}>
          <div className="search-container" style={{ flex: '2', minWidth: '250px' }}>
            <input 
              type="text" 
              placeholder="Search by name, location, or specialization" 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ 
                width: '100%', 
                padding: '0.8rem', 
                borderRadius: '4px', 
                border: '1px solid #ddd',
                fontSize: '1rem'
              }}
            />
          </div>
          
          <div className="filter-container" style={{ flex: '1', minWidth: '200px' }}>
            <select 
              value={selectedSpecialization}
              onChange={(e) => setSelectedSpecialization(e.target.value)}
              style={{ 
                width: '100%',
                padding: '0.8rem', 
                borderRadius: '4px', 
                border: '1px solid #ddd',
                fontSize: '1rem',
                backgroundColor: 'white'
              }}
            >
              <option value="" disabled>Filter by specialization</option>
              {specializations.map(specialization => (
                <option key={specialization} value={specialization}>
                  {specialization}
                </option>
              ))}
            </select>
          </div>
          
          <div className="sort-container" style={{ flex: '1', minWidth: '200px' }}>
            <select 
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{ 
                width: '100%',
                padding: '0.8rem', 
                borderRadius: '4px', 
                border: '1px solid #ddd',
                fontSize: '1rem',
                backgroundColor: 'white'
              }}
            >
              <option value="" disabled>Sort by</option>
              <option value="name">Name</option>
              <option value="location">Location</option>
              <option value="experience">Experience</option>
            </select>
          </div>
        </div>
        
        <div style={{ 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem'
        }}>
          <div>
            <h2 style={{ marginBottom: '0.5rem', fontSize: '1.5rem' }}>Professional Directory</h2>
            <p>{filteredAndSortedProfessionals.length} professionals found</p>
          </div>
          
          {!notificationClosed && (
            <button 
              className="btn btn-primary"
              onClick={handleAddListing}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <span>+</span> {isMember ? 'ADD YOUR LISTING' : 'JOIN BVA TO LIST'}
            </button>
          )}
        </div>
        
        <div className="professionals-grid" style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
          gap: '2rem'
        }}>
          {filteredAndSortedProfessionals.map(professional => (
            <div className="professional-card" key={professional.id} style={{ 
              backgroundColor: 'white',
              borderRadius: '8px',
              overflow: 'hidden',
              boxShadow: 'var(--box-shadow)',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              padding: '1.5rem',
              border: '1px solid #eaeaea'
            }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 10px 20px rgba(0,0,0,0.1)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'var(--box-shadow)';
            }}
            >
              <div style={{ borderLeft: '3px solid var(--primary-color)', paddingLeft: '12px' }}>
                <h3 style={{ 
                  fontSize: '1.3rem', 
                  marginBottom: '0.3rem', 
                  color: 'var(--primary-color)',
                  fontWeight: '600'
                }}>
                  {professional.name}
                </h3>
                <p style={{ 
                  fontSize: '0.9rem', 
                  fontWeight: '500', 
                  color: '#555',
                  marginBottom: '1rem'
                }}>
                  {professional.title || professional.qualification}
                </p>
              </div>

              <div style={{ 
                marginTop: '1rem', 
                padding: '1rem',
                backgroundColor: '#f9f9f9',
                borderRadius: '6px'
              }}>
                <div style={{ display: 'flex', marginBottom: '0.5rem', alignItems: 'flex-start' }}>
                  <span style={{ 
                    fontWeight: '600', 
                    minWidth: '100px', 
                    color: '#444', 
                    fontSize: '0.9rem'
                  }}>
                    Expertise:
                  </span>
                  <span style={{ fontSize: '0.9rem', color: '#333' }}>{professional.expertise}</span>
                </div>
                <div style={{ display: 'flex', marginBottom: '0.5rem', alignItems: 'flex-start' }}>
                  <span style={{ 
                    fontWeight: '600', 
                    minWidth: '100px', 
                    color: '#444', 
                    fontSize: '0.9rem'
                  }}>
                    Location:
                  </span>
                  <span style={{ fontSize: '0.9rem', color: '#333' }}>{professional.location}</span>
                </div>
                <div style={{ display: 'flex', marginBottom: '0.5rem', alignItems: 'flex-start' }}>
                  <span style={{ 
                    fontWeight: '600', 
                    minWidth: '100px', 
                    color: '#444', 
                    fontSize: '0.9rem'
                  }}>
                    Email:
                  </span>
                  {visibleEmails[professional.id] ? (
                    <span style={{ fontSize: '0.9rem', color: '#333' }}>{professional.email}</span>
                  ) : (
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleEmailVisibility(professional.id);
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--primary-color)',
                        cursor: 'pointer',
                        padding: 0,
                        fontSize: '0.9rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <span>View Email</span>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 4C7 4 2.73 7.11 1 11.5C2.73 15.89 7 19 12 19C17 19 21.27 15.89 23 11.5C21.27 7.11 17 4 12 4ZM12 16.5C9.24 16.5 7 14.26 7 11.5C7 8.74 9.24 6.5 12 6.5C14.76 6.5 17 8.74 17 11.5C17 14.26 14.76 16.5 12 16.5ZM12 8.5C10.34 8.5 9 9.84 9 11.5C9 13.16 10.34 14.5 12 14.5C13.66 14.5 15 13.16 15 11.5C15 9.84 13.66 8.5 12 8.5Z" 
                        fill="currentColor"/>
                      </svg>
                    </button>
                  )}
                </div>
                <div style={{ display: 'flex', marginBottom: '0.5rem', alignItems: 'flex-start' }}>
                  <span style={{ 
                    fontWeight: '600', 
                    minWidth: '100px', 
                    color: '#444', 
                    fontSize: '0.9rem'
                  }}>
                    LinkedIn:
                  </span>
                  <a 
                    href={professional.linkedin} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    style={{ 
                      color: '#0077b5', 
                      fontSize: '0.9rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px' 
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span>View Profile</span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M19 19H16V13.7C16 12.87 15.33 12.2 14.5 12.2C13.67 12.2 13 12.87 13 13.7V19H10V10H13V11.2C13.5 10.36 14.59 9.8 15.5 9.8C17.43 9.8 19 11.37 19 13.3V19ZM6.5 8.31C5.5 8.31 4.69 7.5 4.69 6.5C4.69 5.5 5.5 4.69 6.5 4.69C7.5 4.69 8.31 5.5 8.31 6.5C8.31 7.5 7.5 8.31 6.5 8.31ZM8 19H5V10H8V19ZM20 2H4C2.89 2 2 2.89 2 4V20C2 21.1 2.89 22 4 22H20C21.1 22 22 21.1 22 20V4C22 2.89 21.1 2 20 2Z" 
                      fill="#0077b5"/>
                    </svg>
                  </a>
                </div>
              </div>
              
              <div style={{ marginTop: '1.2rem', display: 'flex', justifyContent: 'center' }}>
                <button 
                  className="btn btn-primary" 
                  style={{ 
                    padding: '0.6rem 1.2rem',
                    borderRadius: '4px',
                    fontSize: '0.9rem',
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                  onClick={(e) => handleContactClick(e, professional)}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 4H4C2.9 4 2.01 4.9 2.01 6L2 18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 8L12 13L4 8V6L12 11L20 6V8Z" fill="white"/>
                  </svg>
                  CONTACT
                </button>
              </div>
            </div>
          ))}
        </div>
        
        {filteredAndSortedProfessionals.length === 0 && (
          <div style={{ 
            textAlign: 'center', 
            padding: '3rem', 
            backgroundColor: '#f8f9fa', 
            borderRadius: '8px',
            marginTop: '2rem'
          }}>
            <h3 style={{ marginBottom: '1rem', color: 'var(--primary-color)' }}>
              No professionals found matching your criteria
            </h3>
            <p>
              Try adjusting your search terms or filters to find professionals in our network.
            </p>
          </div>
        )}
        
        <div style={{ marginTop: '3rem' }}>
          <h2>JOIN OUR PROFESSIONAL NETWORK</h2>
          <p>
            Are you a valuation professional looking to connect with peers and expand 
            your network? Join the Bangalore Valuers' Association to be listed in our 
            professional directory and access a range of benefits designed to support 
            your professional growth.
          </p>
          <div style={{ 
            backgroundColor: '#002147', 
            color: 'white', 
            padding: '2rem', 
            borderRadius: '8px', 
            marginTop: '1.5rem',
            textAlign: 'center'
          }}>
            <h3 style={{ marginBottom: '1rem', fontSize: '1.5rem' }}>
              Become a Member Today
            </h3>
            <p style={{ marginBottom: '1.5rem', maxWidth: '800px', margin: '0 auto 1.5rem' }}>
              Join our community of valuation professionals and take advantage of 
              networking opportunities, educational programs, and resources to enhance 
              your practice.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <Link to="/membership">
                <button className="btn btn-secondary" >JOIN BVA</button>
              </Link>
              {isMember && (
                <button className="btn btn-light" 
                  style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', border: '1px solid white' }} 
                  onClick={handleAddListing}
                >
                  ADD YOUR LISTING ({memberListingPrice > 0 ? `₹${memberListingPrice.toLocaleString('en-IN')}` : 'Free'})
                </button>
              )}
            </div>
            {!isMember && (
              <div style={{ 
                marginTop: '1rem', 
                backgroundColor: 'rgba(255,255,255,0.1)', 
                padding: '0.5rem', 
                borderRadius: '4px',
                maxWidth: '600px',
                margin: '1rem auto 0'
              }}>
                <strong>Join BVA to access professional listing opportunities!</strong>
              </div>
            )}
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}

export default ProfessionalsPage;